import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow, PrimaryCTA, SectionNum } from "@/components/ui-bits";
import api from "@/lib/api";
import { FileText, ArrowUpRight } from "lucide-react";

export default function Resources() {
  const [categories, setCategories] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/resource-categories"), api.get("/resources")])
      .then(([c, r]) => {
        setCategories(c.data);
        setResources(r.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const grouped = categories.map((c) => ({
    ...c,
    items: resources.filter((r) => r.category === c.slug),
  }));

  return (
    <Layout>
      <SEO
        title="Guides"
        description="A reference library from Shelton Law PLLC — practical guides on LLC agreements, securities exemptions, founder equity, capital accounts, cap tables, hiring, and SaaS agreements."
        path="/resources"
      />
      <section className="border-b border-hairline" data-testid="resources-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-16">
          <Eyebrow>Guides</Eyebrow>
          <h1 className="display-1 text-ink mt-6 max-w-4xl">Reference library.</h1>
          <p className="mt-8 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
            A collection of practical guides on topics that come up in the firm&apos;s practice. The materials are provided for informational purposes only and do not constitute legal advice.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 space-y-20" data-testid="resources-list">
        {loading && <p className="text-ink-soft" data-testid="resources-loading">Loading…</p>}
        {!loading && grouped.map((cat, idx) => (
          <ResourceCategory
            key={cat.slug}
            category={cat}
            number={String(idx + 1).padStart(2, "0")}
          />
        ))}
      </section>

      <section className="bg-ink text-ivory" data-testid="resources-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-20 text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-5">For Further Inquiries</p>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
            Questions on these materials may be directed to the firm.
          </h2>
          <div className="mt-10 flex justify-center">
            <PrimaryCTA to="/contact" testId="resources-cta-button">Contact the Firm</PrimaryCTA>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function ResourceCategory({ category, number }) {
  const isEmpty = category.items.length === 0;
  return (
    <div data-testid={`resources-category-${category.slug}`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <SectionNum>{number}</SectionNum>
          <h2 className="font-serif text-3xl text-ink mt-3 leading-tight">{category.name}</h2>
          <p className="mt-4 text-ink-soft text-sm leading-relaxed max-w-md">{category.description}</p>
        </div>
        <div className="lg:col-span-8">
          {isEmpty ? (
            <div className="border border-dashed border-hairline p-10" data-testid={`resources-empty-${category.slug}`}>
              <FileText className="w-7 h-7 text-ink-mid mb-3" strokeWidth={1.5} />
              <p className="font-serif text-xl text-ink mb-2">Additional materials forthcoming.</p>
              <p className="text-ink-soft text-sm leading-relaxed max-w-lg">
                Guides are added to this section as topics arise across active engagements.
              </p>
            </div>
          ) : (
            <ul className="border-t border-hairline">
              {category.items.map((item) => (
                <ResourceItem
                  key={item.id}
                  item={item}
                  testId={`resource-${category.slug}-${slugifyTitle(item.title)}`}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ResourceItem({ item, testId }) {
  return (
    <li className="border-b border-hairline">
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className="grid grid-cols-12 gap-6 items-baseline py-6 hover:bg-ivory-alt transition-colors -mx-3 px-3 group"
        data-testid={testId}
      >
        <div className="col-span-2 sm:col-span-1 self-start pt-2">
          <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-brass-deep">{item.format || "PDF"}</span>
        </div>
        <div className="col-span-10 sm:col-span-8 min-w-0">
          <h3 className="font-serif text-2xl text-ink leading-tight group-hover:text-brass transition-colors">{item.title}</h3>
          <p className="mt-2 text-ink-soft text-sm leading-relaxed">{item.description}</p>
        </div>
        <div className="hidden sm:flex col-span-3 justify-end items-baseline gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-ink-soft group-hover:text-brass transition-colors">
          Download <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
        </div>
      </a>
    </li>
  );
}

function slugifyTitle(t) {
  return t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
